import React, { useEffect, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress, Alert, Snackbar } from '@mui/material';
import { useAuth } from './contexts/AuthContext';
import { useWebSocket } from './contexts/WebSocketContext';

// Layout y login se cargan siempre
import LoginPage from './pages/LoginPage';
import DashboardLayout from './components/layout/DashboardLayout';
import ErrorBoundary from './components/common/ErrorBoundary';

// Lazy loading de páginas — se cargan solo cuando el usuario navega a ellas
const DashboardHome = React.lazy(() => import('./pages/DashboardHome'));
const ConversationsPage = React.lazy(() => import('./pages/ConversationsPage'));
const ConversationDetail = React.lazy(() => import('./pages/ConversationDetail'));
const MetricsPage = React.lazy(() => import('./pages/MetricsPage'));
const AgentsPage = React.lazy(() => import('./pages/AgentsPage'));
const ConfigurationPage = React.lazy(() => import('./pages/ConfigurationPage'));
const DebtBotConfigurationPage = React.lazy(() => import('./pages/DebtBotConfigurationPage'));
const AuditLogsPage = React.lazy(() => import('./pages/AuditLogsPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const WindowDashboardPage = React.lazy(() => import('./pages/WindowDashboardPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));

// Componente de carga
const LoadingScreen: React.FC = () => (
  <Box
    display="flex"
    flexDirection="column"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
    bgcolor="background.default"
  >
    <CircularProgress size={60} thickness={4} />
    <Box mt={2} color="text.secondary" fontSize="1.1rem">
      Cargando Dashboard CRM...
    </Box>
  </Box>
);

// Componente para rutas protegidas
interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission?: string;
  requiredRole?: string | string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermission,
  requiredRole,
}) => {
  const { isAuthenticated, hasPermission, hasRole, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <Box p={3}>
        <Alert severity="error">
          No tienes permisos para acceder a esta sección.
        </Alert>
      </Box>
    );
  }

  if (requiredRole && !hasRole(requiredRole)) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Tu rol no tiene acceso a esta sección.
        </Alert>
      </Box>
    );
  }

  return <>{children}</>;
};

// Componente principal de la aplicación
const App: React.FC = () => {
  const { isAuthenticated, isLoading, error, clearError } = useAuth();
  const { isConnected, joinDashboard, subscribeToMetrics } = useWebSocket();
  // const _location = useLocation();

  // Conectar al WebSocket cuando el usuario esté autenticado
  useEffect(() => {
    if (isAuthenticated && isConnected) {
      // Unirse al dashboard y suscribirse a métricas
      joinDashboard();
      subscribeToMetrics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isConnected]);

  // Mostrar pantalla de carga inicial
  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <ErrorBoundary>
      <Routes>
        {/* Ruta de login */}
        <Route
          path="/login"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <LoginPage />
            )
          }
        />

        {/* Rutas del dashboard */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          {/* Página principal del dashboard */}
          <Route index element={<Suspense fallback={<LoadingScreen />}><DashboardHome /></Suspense>} />

          {/* Conversaciones */}
          <Route path="conversations" element={<Suspense fallback={<LoadingScreen />}><ConversationsPage /></Suspense>} />
          <Route path="conversations/:id" element={<Suspense fallback={<LoadingScreen />}><ConversationDetail /></Suspense>} />

          {/* Dashboard de Ventanas 24h */}
          <Route
            path="windows"
            element={
              <ProtectedRoute requiredRole={["ADMIN", "SUPERVISOR"]}>
                <Suspense fallback={<LoadingScreen />}><WindowDashboardPage /></Suspense>
              </ProtectedRoute>
            }
          />

          {/* Métricas y reportes */}
          <Route
            path="metrics"
            element={
              <ProtectedRoute requiredPermission="view_metrics">
                <Suspense fallback={<LoadingScreen />}><MetricsPage /></Suspense>
              </ProtectedRoute>
            }
          />

          {/* Gestión de agentes */}
          <Route
            path="agents"
            element={
              <ProtectedRoute requiredRole={['ADMIN', 'SUPERVISOR']}>
                <Suspense fallback={<LoadingScreen />}><AgentsPage /></Suspense>
              </ProtectedRoute>
            }
          />

          {/* Configuración del sistema */}
          <Route
            path="configuration"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <Suspense fallback={<LoadingScreen />}><ConfigurationPage /></Suspense>
              </ProtectedRoute>
            }
          />

          {/* Configuración del Bot de Deudas */}
          <Route
            path="debt-bot-configuration"
            element={
              <ProtectedRoute requiredRole="ADMIN">
                <Suspense fallback={<LoadingScreen />}><DebtBotConfigurationPage /></Suspense>
              </ProtectedRoute>
            }
          />

          {/* Logs de auditoría */}
          <Route
            path="audit-logs"
            element={
              <ProtectedRoute requiredPermission="view_audit_logs">
                <Suspense fallback={<LoadingScreen />}><AuditLogsPage /></Suspense>
              </ProtectedRoute>
            }
          />

          {/* Perfil de usuario */}
          <Route path="profile" element={<Suspense fallback={<LoadingScreen />}><ProfilePage /></Suspense>} />
        </Route>

        {/* Redirección por defecto */}
        <Route
          path="/"
          element={
            <Navigate
              to={isAuthenticated ? "/dashboard" : "/login"}
              replace
            />
          }
        />

        {/* Página 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      {/* Snackbar para errores globales */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={clearError}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={clearError}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>

      {/* Indicador de conexión WebSocket */}
      {isAuthenticated && !isConnected && (
        <Box
          position="fixed"
          bottom={16}
          right={16}
          zIndex={1400}
        >
          <Alert severity="warning" variant="filled">
            Reconectando al servidor...
          </Alert>
        </Box>
      )}
    </ErrorBoundary>
  );
};

export default App;
